import { MAZE } from '@somnhold/content';
import { furrowsTrophy, mazeRecord, type BattleState, type CampaignState, heroName } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { linesScene } from './cellars.ts';
import { FURROWS_END, FURROWS_OPENING } from './furrows-texts.ts';
import { incursionResult, type Before } from './incursion-result.ts';
import type { CampaignService } from './service.ts';

/*
 * The Furrows around a run (docs/LABERINTOS.md): Brann's scene in the barracks before the first one, and coming back
 * up, a short word by how it ended and then what each one won (the crossings mapped, the chests, the week's chest).
 */

/** Before the first run: Brann in the barracks. «Go down» opens the waiting room; «Not now» stays in the bastion. */
export const furrowsOpening = (onGo: () => void, onLater: () => void): HTMLElement =>
  linesScene(
    FURROWS_OPENING,
    [
      { label: t('maze.scene.go'), test: 'maze-go-down', run: onGo },
      { label: t('maze.scene.later'), test: 'maze-later', ghost: true, run: onLater },
    ],
    {},
    t('maze.title'),
  );

/** How a run ended, as the end scene and card say it. */
const endOf = (b: BattleState): 'centre' | 'back' | 'torch' | 'fallen' => {
  const o = b.dungeon?.maze?.outcome;
  return o === 'centre' || o === 'back' || o === 'torch' ? o : 'fallen';
};

/**
 * Back up from the Furrows: the closing word, then the end card with my haul and a line with what the run added for
 * everyone. `paidBefore`: who had taken the week's chest before this run.
 */
export const furrowsEnd = (svc: CampaignService, b: BattleState, before: Before, paidBefore: Set<string>, onBack: () => void): HTMLElement => {
  const how = endOf(b);
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const card = (): HTMLElement => {
    const rec = mazeRecord(svc.state, svc.day());
    const lines: HTMLElement[] = [];
    // What the server made of this run (the state is fresh by the time the card shows).
    const ev = [...svc.state.events].reverse().find((e) => e.kind === 'maze');
    // What this run added to the group's map: crossings (and the share of the labyrinth), camps, gates, chests.
    const n = Number(ev?.data?.found ?? 0);
    const pct = Math.round(Number(ev?.data?.mapBp ?? 0) / 100);
    const camps = Number(ev?.data?.camps ?? 0);
    const gates = Number(ev?.data?.gates ?? 0);
    const chests = Number(ev?.data?.chests ?? 0);
    if (n > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.map }), t('maze.end.found', { n, p: pct, xp: n * MAZE.xpPerCrossing })));
    if (camps > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.campaign }), t(camps === 1 ? 'maze.end.campsOne' : 'maze.end.camps', { n: camps })));
    if (gates > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.lock }), t(gates === 1 ? 'maze.end.gatesOne' : 'maze.end.gates', { n: gates })));
    if (chests > 0) lines.push(h('li.up', {}, h('span', { html: ICONS.chest }), t('maze.end.chests', { n: chests })));
    if (!lines.length) lines.push(h('li', {}, h('span', { html: ICONS.map }), t('maze.end.foundNone')));
    if (how === 'centre') {
      const paid = rec.paid.filter((pid) => !paidBefore.has(pid));
      for (const hero of b.heroes) if (paid.includes(hero.pid)) lines.push(h('li.up', {}, h('span', { html: ICONS.chest }), t('maze.end.week', { name: names[hero.pid] || hero.name, xp: MAZE.weekly.xp })));
      const away = paid.filter((pid) => !b.heroes.some((x) => x.pid === pid)).map((pid) => names[pid] ?? '').filter(Boolean);
      if (away.length) lines.push(h('li', {}, h('span', { html: ICONS.hero }), t('maze.end.weekAway', { names: away.join(', ') })));
    }
    const extra: HTMLElement[] = [h('div.exp-end-party', { 'data-test': 'maze-end-party' }, h('h4', {}, t('maze.end.party')), h('ul.ie-lines', {}, ...lines))];
    return incursionResult(svc.state, svc.me, before, { won: how !== 'fallen', place: t('maze.title'), onBack, heading: { title: t(`maze.end.${how}`), sub: t(`maze.end.${how}Sub`) }, extra });
  };
  const holder = h('div.exp-end');
  holder.append(linesScene(FURROWS_END[how], [{ label: t('maze.end.see'), test: 'maze-end-see', run: () => holder.append(card()) }], {}, t('maze.title')));
  return holder;
};

/** The table of the Furrows as the trophy card draws it: the carving in dragon red, the lanterns and the cord once earned. */
const tableArt = (posts: boolean, sash: boolean, up: boolean): string => `<svg viewBox="0 0 120 120" aria-hidden="true">
<defs><radialGradient id="tfg" cx="50%" cy="60%" r="55%"><stop offset="0" stop-color="#ff4f3a" stop-opacity="${up ? (sash ? 0.4 : 0.26) : 0.06}"/><stop offset="1" stop-color="#ff4f3a" stop-opacity="0"/></radialGradient></defs>
<rect width="120" height="120" rx="10" fill="#0d0f13"/><circle cx="60" cy="68" r="52" fill="url(#tfg)"/>
<g opacity="${up ? 1 : 0.35}"><path d="M18 72l42-16 42 16-42 16z" fill="#5a5d65" stroke="#2b2d33" stroke-width="1.5"/>
<path d="M18 72v8l42 16v-8zM102 72v8l-42 16v-8z" fill="#3a3c42"/>
<g stroke="#ff8a6e" stroke-width="1.6" fill="none" stroke-linecap="round"><path d="M34 72l26-10 26 10M42 72l18-7 18 7M50 72l10-4M70 72l-6-2"/><path d="M60 62v6M48 69l6 2"/></g>
<path d="M60 88v-8l6-3" stroke="#ffd0c4" stroke-width="2.2" fill="none" stroke-linecap="round"/><circle cx="66" cy="76" r="2.4" fill="#ff4f3a"/>
<path d="M92 70V40l5-4 5 4v30z" fill="#4a4c53" stroke="#2b2d33"/><rect x="91" y="50" width="12" height="3" fill="#ff4f3a"/>
${posts ? '<g stroke="#3a2a20" stroke-width="2.5"><path d="M22 76V40M98 82V44" /></g><g fill="#ff4f3a"><rect x="19" y="38" width="6" height="7" rx="1"/><rect x="95" y="42" width="6" height="7" rx="1"/></g>' : ''}
${sash ? '<path d="M91 58q6 3 12 0" stroke="#ff4f3a" stroke-width="3" fill="none"/><path d="M96 60l-3 10 3-2 1 3zM99 60l3 10-3-2-1 3z" fill="#ff4f3a"/>' : ''}</g></svg>`;

/**
 * The table's trophy: how it looks by the keep and what is left to earn — the table (once), the lanterns (three weeks),
 * the red cord (a week with nobody falling). For the keep's sheet and the Furrows' card.
 */
export const tableTrophyBox = (s: CampaignState, compact = false): HTMLElement => {
  const m = s.maze ?? { wins: 0, flawless: 0 };
  const look = furrowsTrophy(s);
  const need = MAZE.trophy.postsAt;
  const steps: [boolean, string, string][] = [
    [m.wins >= 1, t('maze.trophy.table'), t('maze.trophy.tableHow')],
    [m.wins >= need, t('maze.trophy.posts'), t('maze.trophy.postsHow', { n: Math.min(m.wins, need), need })],
    [m.flawless > 0, t('maze.trophy.sash'), t('maze.trophy.sashHow')],
  ];
  return h(
    `section.trophy${compact ? '.compact' : ''}${look ? '.won' : ''}`,
    { 'data-test': 'table-trophy' },
    h('div.trophy-art', { html: tableArt(!!look?.posts, !!look?.sash, !!look) }),
    h(
      'div.trophy-body',
      {},
      h('span.label', {}, t('maze.trophy.label')),
      h('h4', {}, t('maze.trophy.title')),
      h('p.small', {}, look ? (m.wins === 1 ? t('maze.trophy.countOne', { f: m.flawless }) : t('maze.trophy.count', { n: m.wins, f: m.flawless })) : t('maze.trophy.notYet')),
      h('ul.trophy-steps', {}, ...steps.map(([ok, name, how]) => h(`li${ok ? '.ok' : ''}`, {}, h('span.trophy-mark', { html: ok ? ICONS.check : ICONS.lock }), h('span', {}, h('b', {}, name), h('span.small', {}, how))))),
    ),
  );
};
